import type { PageAdConfig } from 'interfaces/ads/Ad';
import type CustomWindow from 'interfaces/Utils';
import Script from 'next/script';
import { UserContext } from 'providers/UserProvider';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAdContext } from 'services/Ad';
import type Nativo from 'services/Nativo/entities';
import type { NativoCmd } from 'services/Nativo/entities';

interface AdManagerProps {
  pageAdConfig: PageAdConfig;
  setAdContextValue: (value: any) => void;
}

declare let window: CustomWindow;

const AdManager: React.FC<AdManagerProps> = ({ pageAdConfig, setAdContextValue }) => {
  const [isArctagReady, setIsArctagReady] = useState(false);
  const [shouldStartNativo, setShouldStartNativo] = useState(false);
  const [isNativoStarted, setIsNativoStarted] = useState(false);
  const { personalizedAds } = useContext(UserContext);
  const adContextValue = useAdContext(pageAdConfig);
  setAdContextValue(adContextValue);

  // We should probably change the name of this variable; CLP? Campaign Landing Page?
  const CLPcheck =
    typeof window !== 'undefined' &&
    (window.location.search.includes('prx_t') || window.location.search.includes('ntv_adpz'));

  const noNativoTags = ['Personal Finance Evergreen', 'Health Evergreen', 'no-nativo'];

  const noNativoAds =
    noNativoTags.some((tag) => pageAdConfig.tags?.includes(tag)) || pageAdConfig.topics?.includes('no-nativo');

  useEffect(() => {
    // avoid race condition and intialize nativo cmd
    if (typeof window !== 'undefined') {
      window.ntv = window.ntv || ({} as NativoCmd);
      window.ntv.cmd = window.ntv.cmd || [];
    }

    // Set Nativo targeting
    const kvpMap: { [key: string]: string } = {};
    kvpMap.topics = pageAdConfig.topics ? pageAdConfig.topics.join(',') : '';
    kvpMap.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    window.ntvConfig = window.ntvConfig || ({} as Nativo);
    window.ntvConfig.keyValues = kvpMap;

    if (window.ntvConfig && window.ntvConfig.keyValues && personalizedAds) {
      window.ntvConfig.keyValues.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    }

    // Listen for ArctagReady event to know when Arcspan was initialized
    const handleArctagReady = () => {
      setIsArctagReady(true);
    };

    window.addEventListener('arctagReady', handleArctagReady);

    // Attach callbacks to window object
    // The ArcSpan injection script in /Arcspan/index.tsx will call these handlers
    if (typeof window !== 'undefined') {
      window.onArcspanScriptLoad = () => {
        setShouldStartNativo(true);
      };

      window.onArcspanScriptError = () => {
        setShouldStartNativo(true);
      };
    }
    return () => {
      // Clean up the global functions
      if (typeof window !== 'undefined') {
        window.removeEventListener('arctagReady', handleArctagReady);
        delete window.onArcspanScriptLoad;
        delete window.onArcspanScriptError;
      }
    };
  }, [pageAdConfig.topics, personalizedAds]);

  useEffect(() => {
    if (noNativoAds) {
      return;
    }

    if (isArctagReady && personalizedAds) {
      let arcspanValue = '';

      if (window?.arcobj2?.activation) {
        arcspanValue = window.arcobj2.activation['Google Ad Manager']?.join(',') || '';
      }
      window.ntvConfig = window.ntvConfig || { keyValues: {} };
      window.ntvConfig.keyValues = {
        ...window.ntvConfig.keyValues,
        arcspan: arcspanValue,
      };
    }

    // Initialize Nativo when Arcspan script is ready or loading Arcspan script failed
    // Don't initialize if we're adding Nativo via search query params (since that is already adding the script and calling Start())
    if ((isArctagReady || shouldStartNativo) && !CLPcheck) {
      setIsNativoStarted(true);
    }
  }, [personalizedAds, noNativoAds, isArctagReady, shouldStartNativo, CLPcheck]);

  const [scriptReady, setScriptReady] = useState(
    personalizedAds === true ||
    (typeof document !== 'undefined' && document.cookie.includes('usprivacy=')),
  );
  const scriptReadyRef = useRef(scriptReady);
  scriptReadyRef.current = scriptReady;

  useEffect(() => {
    if (personalizedAds === true) {
      if (!scriptReadyRef.current) {
        setScriptReady(true);
      }
      return;
    }
    if (scriptReadyRef.current || isNativoStarted || noNativoAds || personalizedAds === null) {
      return;
    }
    const timer = setInterval(() => {
      if (document.cookie.includes('usprivacy=')) {
        setScriptReady(true);
        setIsNativoStarted(true);
        clearInterval(timer);
      }
    }, 200);
    const safety = setTimeout(() => {
      clearInterval(timer);
    }, 5000);
    return () => {
      clearInterval(timer);
      clearTimeout(safety);
    };
  }, [personalizedAds, noNativoAds, isNativoStarted]);

  useEffect(() => {
    if (!isNativoStarted) {
      return;
    }

    window.ntv.cmd.push(() => {
      window.PostRelease?.Start();
    });
  }, [isNativoStarted]);

  return (
    <>
      {!noNativoAds && scriptReady && (
        <Script
          id='nativo-js'
          src='https://s.ntv.io/serve/load.js'
          strategy='afterInteractive'
          data-ntv-set-no-auto-start
          {...(CLPcheck && { 'data-ntv-pub-injection': true })}
        />
      )}

      {CLPcheck && (
        <Script
          id='nativo-iframe'
          src='https://assets.promotedarticle.com/iframeInjection.js'
          onLoad={() => {
            if (window.ntv && window.ntv.cmd) {
              window.ntv.cmd.push(() => {
                window.PostRelease?.Start();
              });
            }
          }}
        />
      )}
    </>
  );
};

export default AdManager;
